import { Box, Button, Typography, useTheme } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import CommentsWrap from '@/components/comments_components/CommentsWrap';
import RequiredIndicator from '@/components/common/RequiredIndicator';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { AttachmentProps } from '@/models/Attachment';
import { EventProps } from '@/models/Event';
import { QuestionProps } from '@/models/Question';

import DataUpload from './DataUpload';
import { ApplicationStatus } from '@/utils/constants';

const DataUploadWrap = ({
  question,
  eventInfo,
}: {
  question: QuestionProps;
  eventInfo?: EventProps | null;
}) => {
  const theme = useTheme();
  const { answers, currentApplication, canEdit } = useContext(CurrentApplicationContext);
  const [hasError, setHasError] = useState<boolean>(false);

  const { translatedText, localeLanguageCode } = useContext(LocaleContext);

  const [showNewUpload, setShowNewUpload] = useState<boolean>(false);
  const [hasPendingUploads, setHasPendingUploads] = useState<boolean>(false);
  const [currentFiles, setCurrentFiles] = useState<AttachmentProps[]>([]);

  const getSummaryPdf = question.id == 2186 || question.id == 2867;
  const questionAnswer = useMemo(() => answers[question.id], [answers, question.id]);
  const wrapperWidth = currentFiles.length > 0 || getSummaryPdf ? '50dvw' : '35dvw';

  useEffect(() => {
    let files: AttachmentProps[] = [];

    if (questionAnswer?.files != null) {
      if (question.questionType == 'event' && eventInfo?.id != null) {
        files = questionAnswer.files.filter(
          (file: AttachmentProps) => file.eventId == eventInfo.id
        );
      } else {
        files = questionAnswer.files;
      }
    }

    setCurrentFiles(files);

    setShowNewUpload(files.length === 0 || hasPendingUploads);
  }, [eventInfo?.id, hasPendingUploads, question?.questionType, questionAnswer]);

  const dataWrapUploadFileCheck = (file: boolean) => {
    setHasError(!file);
  };

  return (
    <Box
      display='flex'
      width='100%'
      justifyContent='space-between'
      alignItems='start'
      id='UPLOAD-WRAP'
      marginTop='20px'
    >
      <Box
        width={wrapperWidth}
        border={
          question.id == 2186 || question.id == 2867
            ? 'none'
            : question?.questionNeeded &&
                hasError &&
                currentApplication.status != null &&
                currentApplication.status < ApplicationStatus.ApplicationApproved
              ? `1px solid ${theme.palette.error.main}`
              : `1px solid ${theme.palette.sandShade3.main}`
        }
        borderRadius='10px'
        padding='20px'
        flexGrow={0}
        sx={{ backgroundColor: theme.palette.sandTint.main }}
      >
        {question.questionType == 'event' &&
        question?.eventType != null &&
        localeLanguageCode != null ? (
          <Typography variant='body2' sx={{ mb: 0, mt: 0 }}>
            {`${translatedText.dataUpload.labelEventProof}${question?.eventType.nameTranslations[localeLanguageCode]}`}
            {question?.questionNeeded ? <RequiredIndicator /> : ''}
          </Typography>
        ) : (
          <Typography variant='body2' sx={{ mb: 0, mt: 0 }}>
            {question.questionText}
            {question.questionNeeded ? <RequiredIndicator /> : ''}
          </Typography>
        )}

        <Box sx={{ marginTop: '8px' }}>
          <Typography variant='subtitle3' color='textSecondary'>
            {question.questionNote}
          </Typography>
        </Box>
        {currentFiles?.length > 0 &&
          currentFiles?.map((upload: AttachmentProps) => (
            <DataUpload
              key={`${question.id}-${upload.fileName}`}
              question={question}
              fileObject={upload}
              dataWrapUploadFileCheck={dataWrapUploadFileCheck}
              eventInfo={eventInfo}
              disabled={!canEdit}
            />
          ))}

        {showNewUpload ? (
          <DataUpload
            key={`${question.id}-new`}
            disabled={!canEdit}
            question={question}
            fileObject={{
              eventId: eventInfo != null ? eventInfo.id : null,
            }}
            dataWrapUploadFileCheck={dataWrapUploadFileCheck}
            eventInfo={eventInfo}
            onPendingUploadsChange={setHasPendingUploads}
          />
        ) : (
          <Button
            disabled={!canEdit}
            sx={{
              marginTop: '15px',
              display: getSummaryPdf ? 'none' : 'block',
            }}
            onClick={() => setShowNewUpload(true)}
            size='small'
            variant='secondary'
          >
            {question.id == 2186 || question.id == 2867
              ? translatedText.dataUpload.labelGetPdfSummary
              : translatedText.dataUpload.labelAddFile}
          </Button>
        )}
      </Box>
      {question.questionType != 'event' && <CommentsWrap question={question} />}
    </Box>
  );
};

export default DataUploadWrap;
